// Copyright (C) 2020-2022 Intel Corporation
// Copyright (C) 2022-2024 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import React from 'react';
import { RouteComponentProps } from 'react-router';
import { withRouter } from 'react-router-dom';
import Text from 'antd/lib/typography/Text';
import { Row, Col } from 'antd/lib/grid';
import Button from 'antd/lib/button';
import { LoadingOutlined, MoreOutlined } from '@ant-design/icons';
import Dropdown from 'antd/lib/dropdown';
import Progress from 'antd/lib/progress';
import Badge from 'antd/lib/badge';
import moment from 'moment';

import { Reference, RQStatus, Task } from 'cvat-core-wrapper';
import ActionsMenuContainer from 'containers/actions-menu/actions-reference-menu';
import Preview from 'components/common/preview';
import { ActiveInferenceRef, PluginComponent } from 'reducers';
import AutomaticAnnotationProgress from './automatic-annotation-progress';
import Link from 'antd/lib/typography/Link';
import ReferenceLastJob from './reference-last-job';

export interface ReferenceItemProps {
    referenceInstance: any;
    deleted: boolean;
    isJobOpen?: boolean;
    activeInference: ActiveInferenceRef | null;
    ribbonPlugins: PluginComponent[];
    cancelAutoAnnotation(): void;
    updateReferenceInState(reference: Reference): void;
}

interface State {
        importingState: {
        state: RQStatus | null;
        message: string;
        progress: number;
    } | null;
}

class ReferenceItemComponent extends React.PureComponent<ReferenceItemProps & RouteComponentProps, State> {
    #isUnmounted: boolean;

    constructor(props: ReferenceItemProps & RouteComponentProps) {
        super(props);
        const { referenceInstance } = props;
        this.#isUnmounted = false;
        this.state = {
            importingState:
                referenceInstance.size > 0 ?
                    null :
                    {
                        state: null,
                        message: 'Request current progress',
                        progress: 0,
                    },
        };
    }

    public componentDidMount(): void {
        const { referenceInstance, updateReferenceInState } = this.props;
        const { importingState } = this.state;


        if (importingState !== null && typeof referenceInstance.listenToCreate === 'function') {
            referenceInstance
                .listenToCreate((state: RQStatus, progress: number, message: string) => {
                    if (!this.#isUnmounted) {
                        this.setState({
                            importingState: {
                                message,
                                progress: Math.floor(progress * 100),
                                state,
                            },
                        });
                    }
                })
                .then((createdReference: Reference) => {
                    if (!this.#isUnmounted) {
                        this.setState({ importingState: null });

                        setTimeout(() => {
                            if (!this.#isUnmounted) {
                                // check again, because the component may be unmounted to this moment
                                const { referenceInstance: currentReferenceInstance } = this.props;
                                if (currentReferenceInstance.size !== createdReference.size) {
                                    // update state only if it was not updated anywhere else
                                    // for example in createReferenceAsync
                                    updateReferenceInState(createdReference);
                                }
                            }
                        }, 1000);
                    }
                });
        }
    }

    public componentWillUnmount(): void {
        this.#isUnmounted = true;
    }

    private renderPreview(): JSX.Element {
        const { referenceInstance } = this.props;
        return (
            <Col span={4}>
                <Preview
                    reference={referenceInstance}
                    loadingClassName='cvat-reference-item-loading-preview'
                    emptyPreviewClassName='cvat-reference-item-empty-preview'
                    previewWrapperClassName='cvat-reference-item-preview-wrapper'
                    previewClassName='cvat-reference-item-preview'
                />
            </Col>
        );
    }

    private renderDescription(): JSX.Element {
        // Reference info
        const { referenceInstance, history, location } = this.props;
        const { id, taskId } = referenceInstance;
        const owner = referenceInstance.owner ? referenceInstance.owner.username : null;
        const updated = moment(referenceInstance.updatedDate).fromNow();
        const created = moment(referenceInstance.createdDate).format('MMMM Do YYYY');

        // Get and truncate a reference name
        const name = `${referenceInstance.name.substring(0, 70)}${referenceInstance.name.length > 70 ? '...' : ''}`;
        return (
            <Col span={10} className='cvat-reference-item-description'>
                <Text strong type='secondary' className='cvat-item-reference-id'>{`#${id}: `}</Text>
                <Link
                    href={`/references/${id}`}
                    onClick={(e: React.MouseEvent): void => {
                        e.preventDefault();
                        history.push(`/references/${id}`);
                    }}
                    strong className='cvat-item-reference-name'>
                    {name}
                </Link>
                <br />
                <Text>Task:</Text>
                {taskId.map((task: Task) => (
                    <Button
                        className='cvat-item-reference-project'
                        type='link'
                        value={`/tasks/${task}`}
                        href={`/tasks/${task}`}
                        onClick={(event: React.MouseEvent): void => {
                            event.preventDefault();
                            if (location.pathname.startsWith('/tasks')) {
                                window.location.reload();
                            } else {
                                history.push(`/tasks/${task}`);
                            }
                        }}
                    >
                        #
                        {task}
                    </Button>
                ))}
                <br />
                {owner && (
                    <>
                        <Text type='secondary'>{`Created ${owner ? `by ${owner}` : ''} on ${created}`}</Text>
                        <br />
                    </>
                )}
                <Text type='secondary'>{`Last updated ${updated}`}</Text>
            </Col>
        );
    }

    private renderProgress(): JSX.Element {
        const { referenceInstance, activeInference, cancelAutoAnnotation } = this.props;
        const { importingState } = this.state;

        if (importingState) {
            let textType: 'success' | 'danger' = 'success';
            if (!!importingState.state && [RQStatus.FAILED, RQStatus.UNKNOWN].includes(importingState.state)) {
                textType = 'danger';
            }

            return (
                <Col span={7}>
                    <Row>
                        <Col span={24} className='cvat-reference-item-progress-wrapper'>
                            <div>
                                <Text
                                    strong
                                    type={[RQStatus.QUEUED, null].includes(importingState.state) ? undefined : textType}
                                >
                                    {`\u2022 ${importingState.message || importingState.state}`}
                                    {!!importingState.state &&
                                        [RQStatus.QUEUED, RQStatus.STARTED].includes(importingState.state) && (
                                        <LoadingOutlined />
                                    )}
                                </Text>
                            </div>
                            <Progress
                                percent={importingState.progress}
                                strokeColor='#1890FF'
                                strokeWidth={5}
                                size='small'
                            />
                        </Col>
                    </Row>
                </Col>
            );
        }
        // Count number of jobs and performed jobs
        const numOfJobs = referenceInstance.progress.totalJobs;
        const numOfCompleted = referenceInstance.progress.completedJobs;
        const numOfValidation = referenceInstance.progress.validationJobs;
        const numOfAnnotation = referenceInstance.progress.annotationJobs;

        // Progress appearance depends on number of jobs
        const jobsProgress = ((numOfCompleted + numOfValidation) * 100) / numOfJobs;

        return (
            <Col span={7}>
                <Row>
                    <Col span={24} className='cvat-reference-item-progress-wrapper'>
                        <div>
                            {numOfCompleted > 0 && (
                                <Text strong className='cvat-reference-completed-progress'>
                                    {`\u2022 ${numOfCompleted} done `}
                                </Text>
                            )}

                            {numOfValidation > 0 && (
                                <Text strong className='cvat-reference-validation-progress'>
                                    {`\u2022 ${numOfValidation} on review `}
                                </Text>
                            )}

                            {numOfAnnotation > 0 && (
                                <Text strong className='cvat-reference-annotation-progress'>
                                    {`\u2022 ${numOfAnnotation} annotating `}
                                </Text>
                            )}
                            <Text strong type='secondary'>
                                {`\u2022 ${numOfJobs} total`}
                            </Text>
                        </div>
                        <Progress
                            percent={jobsProgress}
                            success={{
                                percent: (numOfCompleted * 100) / numOfJobs,
                            }}
                            strokeColor='#1890FF'
                            showInfo={false}
                            strokeWidth={5}
                            size='small'
                        />
                    </Col>
                </Row>
                <AutomaticAnnotationProgress
                    activeInference={activeInference}
                    cancelAutoAnnotation={cancelAutoAnnotation}
                />
            </Col>
        );
    }

    private renderNavigation(): JSX.Element {
        const { importingState } = this.state;
        const { referenceInstance, history, isJobOpen } = this.props;
        const { id } = referenceInstance;

        const onViewAnalytics = (): void => {
            history.push(`/references/${referenceInstance.id}/analytics`);
        };

        return (
            <Col span={3}>
                <Row justify='end'>
                    <Col>
                        {isJobOpen ? ( <ReferenceLastJob /> ) : (
                            <Button
                                disabled={!!importingState}
                                className='cvat-item-open-reference-button'
                                type='primary'
                                size='large'
                                ghost
                                href={`/references/${id}`}
                                onClick={(e: React.MouseEvent): void => {
                                    e.preventDefault();
                                    history.push(`/references/${id}`);
                                }}
                            >
                                Open
                            </Button>
                        )}
                    </Col>
                </Row>
                <Row justify='end'>
                    <Dropdown
                        trigger={['click']}
                        destroyPopupOnHide
                        overlay={(
                            <ActionsMenuContainer
                                referenceInstance={referenceInstance}
                                onViewAnalytics={onViewAnalytics}
                            />
                        )}
                    >
                        <Col className='cvat-item-open-reference-actions'>
                            <Text className='cvat-text-color'>Actions</Text>
                            <MoreOutlined className='cvat-menu-icon' />
                        </Col>
                    </Dropdown>
                </Row>
            </Col>
        );
    }

    public render(): JSX.Element {
        const { deleted, ribbonPlugins, referenceInstance } = this.props;

        if (Object.keys(referenceInstance).length === 1 && 'taskIds' in referenceInstance) {
            return null;
        }

        const style = {};
        if (deleted) {
            (style as any).pointerEvents = 'none';
            (style as any).opacity = 0.5;
        }

        const ribbonItems = ribbonPlugins
            .filter((plugin) => plugin.data.shouldBeRendered(this.props, this.state))
            .map((plugin) => ({ component: plugin.component, weight: plugin.data.weight }));

        return (
            <Badge.Ribbon
                style={{ visibility: ribbonItems.length ? 'visible' : 'hidden' }}
                className='cvat-reference-item-ribbon'
                placement='start'
                text={(
                    <div>
                        {ribbonItems
                            .sort((item1, item2) => item1.weight - item2.weight)
                            .map((item) => item.component)
                            .map((Component, index) => (
                                <Component key={index} targetProps={this.props} targetState={this.state} />
                            ))}
                    </div>
                )}
            >
                <Row className='cvat-references-list-item' justify='center' align='top' style={{ ...style }}>
                    {this.renderPreview()}
                    {this.renderDescription()}
                    {this.renderProgress()}
                    {this.renderNavigation()}
                </Row>
            </Badge.Ribbon>
        );
    }
}

export default withRouter(ReferenceItemComponent);
